import {
  AlertTriangle,
  ArrowLeft,
  ArrowRightLeft,
  ArrowUpRight,
  Briefcase,
  CalendarPlus,
  ClipboardList,
  Clock,
  History,
  MessageCircle,
  MoreHorizontal,
  ShieldCheck,
  Tag,
  UserCheck,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppointmentCreateModal } from '../../../components/AppointmentCreateModal';
import { ConfirmCustomerIdentityModal } from '../../../components/ConfirmCustomerIdentityModal';
import { ConversationReopenModal } from '../../../components/ConversationReopenModal';
import { HandoffInitiateModal } from '../../../components/HandoffInitiateModal';
import { PendencyCreateModal } from '../../../components/PendencyCreateModal';
import { useCapability } from '../../../hooks/use-capability';
import {
  channelLabel,
  initialsOf,
  isReopenableStatus,
  isTerminalStatus,
  relativeTime,
  statusLabel,
  statusTone,
} from '../format';
import { useCloseConversationFlow } from '../hooks/use-close-conversation-flow';
import { useContactRole } from '../hooks/use-contact-role';
import { useQualitySignals } from '../hooks/use-quality-signals';
import { useSetOperationMode } from '../hooks/use-set-operation-mode';

import { ConversationCloseModal } from './ConversationCloseModal';
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal';
import { SESSION_LABELS } from './MetaSessionBlock';
import { ModeBadge } from './ModeBadge';
import { PlaybookManualSwitchModal } from './PlaybookManualSwitchModal';
import { SensitivityBadge } from './SensitivityBadge';

import type { ConversationDetail } from '../../../lib/types';
import type { LucideIcon } from 'lucide-react';

/**
 * Header da Conversa em curso — Tier 1 V2.3 §1.1 (~52px).
 *
 * Elementos (esquerda → direita):
 *   - Avatar pequeno (clicável → IdentityPopover)
 *   - DisplayName (clicável → IdentityPopover)
 *   - Status inline: ●dot verde + "há Xmin"
 *   - ClassificationBadge (condicional ≥ N2; N1 não exibe)
 *   - ModeBadge clicável (r7b §6.2 — popover com descrição do modo)
 *   - Ações inline: Transferir · Pendência · Agendar (todas disabled na F4)
 *   - Menu `⋯`: audit / atalhos (⌘/) / outras
 *   - Botão Encerrar destacado em risco
 *
 * Tier 2 (canal/MetaSession/Bond/Intent/Origin) é relocado para abas
 * conforme V2.3 §1.1 — não aparece no header.
 *
 * r7b refinamentos:
 *   - KeyboardShortcutsModal via ⌘/ (MoreMenu + listener de teclado)
 *   - ModeBadge clicável com popover explicativo
 *   - IdentityPopover enriquecido (canal + último identificador)
 */
export function ConversationHeader({
  conversation,
}: {
  conversation: ConversationDetail;
}): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Voltar para a tela ANTERIOR, não para um destino fixo.
   *
   * Antes era `navigate('/')`, e `/` não é uma tela: é o `RootRedirect`, que
   * manda para a home DO PAPEL — `/supervisor` para quem tem
   * `supervisor-dashboard-read`, `/painel` para quem tem `agent-dashboard-read`,
   * e assim por diante. O botão nunca voltava: ele ia para casa. (Achado do TL
   * navegando, 14/Ago: «ela sempre volta pro painel da equipe».)
   *
   * A conversa é alcançável da Inbox, do Localizador (⌘K), do painel pessoal, das
   * pendências, do painel do supervisor e das mensagens — destino fixo é errado
   * por construção. Isso a distingue dos outros seis botões de voltar do app, que
   * recebem `onBack` do pai porque a tela deles tem UM pai só (Playbook detalhe
   * ← lista de Playbooks). Aqueles estão certos como estão.
   *
   * `location.key === 'default'` é o sinal do React Router para «esta é a
   * primeira entrada desta sessão» — link colado, F5, aba nova. Aí não há para
   * onde voltar, e o fallback é a Caixa de entrada: é onde conversas vivem. NÃO a
   * raiz, que é justamente o comportamento errado de antes.
   */
  const goBack = (): void => {
    if (location.key === 'default') {
      navigate('/inbox');
      return;
    }
    navigate(-1);
  };

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const personName = conversation.person?.displayName ?? '(sem identidade)';
  const classificationLegacy = conversation.classificationLevel ?? 1;
  const mode = conversation.operationMode.current;
  // Fatia 8 — SensitivityBadge dinâmico via classification-agent.
  const signals = useQualitySignals(conversation.id);
  const setOpMode = useSetOperationMode(conversation.id);
  const dynamicLevel = signals.sensitivityLevel;

  // Fatia F — wiring da ação Encerrar. Fluxo compartilhado (T9 Fase 1
  // «um cérebro só»): o Composer abre o MESMO fluxo pelo chip `close`.
  const closeFlow = useCloseConversationFlow(conversation.id);
  const [transferOpen, setTransferOpen] = useState(false);
  const [pendencyOpen, setPendencyOpen] = useState(false);
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  // §2.8 Item 297 — botão "Reabrir conversa" só aparece em terminal + capability
  // `reopen-closed-conversations` (Supervisor+/Gestor/Admin tenant/Admin global).
  const canReopen = useCapability('reopen-closed-conversations');
  // Brief 2B T4 — launcher da Coleta Estruturada (ADR-144). P19: some no Solo
  // (sem a capability). Navega à rota em-conversa que abre o painel de coleta.
  const canCollect = useCapability('structured-collection');
  // ADR-150 — seção "Agendar follow-up" no encerramento (fonte manual do motor
  // SOA; mesma permissão de produto do T8, diferida no tempo).
  const canFollowup = useCapability('initiate-proactive-conversation');
  // S7 PR3 (S6 Condução Governada, Q-J8) — troca manual de playbook pelo
  // atendente comum (≠ force-switch do supervisor, que IGNORA a matriz).
  const canManualSwitchPlaybook = useCapability('playbook-manual-switch');
  const [manualSwitchOpen, setManualSwitchOpen] = useState(false);
  // Frente C (spec 2026-08-12 §3.1) — atalho ao histórico do cliente. Ícone-só
  // (sem contagem): o header tem restrição de largura MEDIDA (vide comentário
  // das ações); a contagem vive no painel (B3) e na ficha. Presente também em
  // conversa terminal — histórico é leitura, não ação de estado.
  // Re-medição PÓS-botão (12/Ago/2026, viewport 1467, coluna 827, todas as caps):
  // vivo = filhos 743px / ações 293px · terminal = filhos 743px / ações 192px —
  // sem overflow, nome do cliente inteiro (102px) nos dois estados.
  const canViewPersonHistory = useCapability('person-read');
  // Id ESCALAR primeiro (personId), ref expandido como fallback: se o serializer
  // entregar personId sem `person` (classe «vínculo mudo»), o atalho não some.
  const historyPersonId = conversation.personId ?? conversation.person?.id ?? null;
  const showHistory = canViewPersonHistory && historyPersonId !== null;

  // Fatia F — conversa em estado terminal não pode ser encerrada de novo
  // (o lifecycle rejeita closed_unresolved → resolved). Desabilita o botão.
  // Fonte única de verdade compartilhada com o Composer (§2.8 read-only).
  const isTerminal = isTerminalStatus(conversation.status);
  /**
   * Terminal ≠ reabrível: `archived` é final (vide `isReopenableStatus`).
   *
   * E não basta o ESTADO: só a conversa MAIS RECENTE da pessoa se reabre
   * (decisão do TL, §5.4 da spec de consulta de episódio). O TL viu o botão
   * oferecido num episódio de 07/08 com a conversa de 11/08 aberta — reabrir ali
   * produziria duas conversas vivas para a mesma pessoa.
   *
   * Quem responde é o SERVIDOR (`reopenable`), não esta tela: recompor a regra
   * aqui a poria em dois lugares. `!== false` porque o campo é opcional por
   * retrocompat — na ausência dele vale a regra antiga, e o engine barra de todo
   * jeito.
   */
  const showReopen =
    canReopen && isReopenableStatus(conversation.status) && conversation.reopenable !== false;

  // r7b — ⌘/ abre KeyboardShortcutsModal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Brief 2A r7b canonical — DS canonical .conv-header layout
  // (Spec standalone.html linhas canvas central). CSS aditivo em
  // global.css fornece layout flex + identity + badges + spacer +
  // actions. Tailwind preservado apenas em containers de popover/dropdown.
  return (
    <header className="conv-header relative">
      {/* Volta — botão sutil à esquerda */}
      <button
        type="button"
        onClick={goBack}
        className="icon-btn"
        aria-label="Voltar"
        title="Voltar"
      >
        <ArrowLeft className="icon" size={16} aria-hidden />
      </button>

      {/* Identity button canonical (avatar pequeno + nome + status inline) */}
      <button
        type="button"
        onClick={() => setPopoverOpen((v) => !v)}
        className="conv-header__identity"
        aria-expanded={popoverOpen}
        aria-haspopup="dialog"
      >
        <span
          className="avatar avatar--sm"
          style={{ background: 'var(--bg-surface-2)', color: 'var(--fg-2)' }}
        >
          {initialsOf(personName)}
        </span>
        <span className="conv-header__name">{personName}</span>
        <StatusInline conversation={conversation} />
      </button>

      {/* Badges canonical agrupados (Class + Mode + Sequence) */}
      <div className="conv-header__badges">
        {/* SensitivityBadge dinâmico (Fatia 8) — fallback para ClassificationBadge legado quando IA ainda não classificou. */}
        {dynamicLevel !== null ? (
          <SensitivityBadge level={dynamicLevel} />
        ) : (
          classificationLegacy >= 2 && <ClassificationBadge level={classificationLegacy} />
        )}

        {/*
         * ModeBadge clicável (r7b) — Task 59 canonical: handler real. Em conversa
         * TERMINAL o selo é informativo, nunca alternável (08/Ago/2026): era o único
         * controle do header sem trava de estado, e o caminho inteiro da troca aceitava
         * H_c numa conversa arquivada — UI, endpoint de modo, listener de delegação e o
         * chokepoint da IA, nenhum olhava o status. Como a IA NÃO passa por
         * `MessagesService.sendOutbound` (cria a Message e emite MESSAGE_SENT direto), o
         * bloqueio de envio do caminho humano nunca cobriu esse caminho. Esta é a
         * primeira das quatro travas; as outras três vivem no engine.
         */}
        <ModeBadge
          mode={mode}
          {...(isTerminal
            ? {}
            : {
                onChange: setOpMode.mutate,
                isPending: setOpMode.isPending,
                canSwitchPlaybook: canManualSwitchPlaybook,
                onSwitchPlaybookClick: () => setManualSwitchOpen(true),
              })}
        />
      </div>

      {/* Spacer canonical — empurra ações para a direita */}
      <div className="conv-header__spacer" />

      {/* Ações inline canonical (icon-btn + divider + btn--header-close) */}
      <div className="conv-header__actions">
        {/*
         * §2.8 revisado (08/Ago/2026) — conversa TERMINAL é read-only: os controles que
         * só operam conversa viva SAEM em vez de ficar cinzas, e voltam inteiros quando
         * ela reabre (é o princípio de Tier 2 do Briefing 2A §1.1: "relocado, não some").
         *
         * Antes ficavam desabilitados, e o custo era de largura: medido no dev (viewport
         * 1472, coluna central de 832px, `getBoundingClientRect`) o header em estado
         * terminal pedia 976px — 144px além da caixa. O flex encolhia a caixa das ações
         * abaixo do conteúdo e os botões, de tamanho fixo, transbordavam: o único botão
         * VIVO ("Reabrir conversa") pintava 26px POR CIMA do painel direito, os ícones
         * caíam de 28px para 18px e o nome do cliente truncava a 38px — tudo para caber
         * o que não funcionava. O estado terminal soma o selo de status (+77px) e o botão
         * Reabrir (+119px) a um header que só tinha ~52px de folga.
         */}
        {!isTerminal && (
          <>
            <InlineAction
              label="Transferir"
              Icon={ArrowRightLeft}
              shortcut="⌘⇧T"
              onClick={() => setTransferOpen(true)}
            />
            <InlineAction
              label="Pendência"
              Icon={Clock}
              shortcut="⌘P"
              onClick={() => setPendencyOpen(true)}
            />
            <InlineAction
              label="Agendar"
              Icon={CalendarPlus}
              onClick={() => setAppointmentOpen(true)}
            />
            {canCollect && (
              <InlineAction
                label="Coleta"
                Icon={ClipboardList}
                onClick={() => navigate(`/atendimento/conversation/${conversation.id}/coleta`)}
              />
            )}
          </>
        )}
        {/*
         * Era «Histórico do cliente», e o rótulo ficou errado quando o fio
         * contínuo passou a ser o histórico (achado do TL, 13/Ago: «esse botão
         * ficou disfuncional depois de tudo que a gente introduziu»).
         *
         * O DESTINO continua valendo — a ficha da pessoa tem 9 seções (visão
         * geral, bonds, carteira, conversas, pendências, agendamentos, fila de
         * espera, resumos IA, bubbles), e conversas é só uma delas. O que se
         * perdeu foi o nome: ele prometia histórico de conversa, que agora está
         * no fio, a um clique daqui. Renomear resolve sem apagar a única porta
         * da conversa para o cadastro da pessoa.
         */}
        {showHistory && (
          <button
            type="button"
            className="icon-btn"
            aria-label="Ficha do cliente"
            title="Ficha do cliente — cadastro, vínculos, agendamentos e pendências"
            onClick={() => navigate(`/pessoas/${historyPersonId}#conversas`)}
          >
            <History className="icon" size={16} aria-hidden />
          </button>
        )}
        {/* O `⋯` fica nos dois estados — audit e atalhos não dependem de conversa viva. */}
        <MoreMenu onOpenShortcuts={() => setShortcutsOpen(true)} />
        {/* Divisor só quando há ação de estado depois dele. */}
        {(!isTerminal || showReopen) && <span className="divider" aria-hidden />}
        {!isTerminal && <CloseConversationButton onClick={closeFlow.openClose} />}
        {/*
         * §2.8 Item 297 — Reabrir conversa: capability presente E estado REABRÍVEL.
         * `archived` é terminal mas não reabre (o endpoint executa `reopenSame`, que exige
         * `isClosing`; a matriz do ADR-054 não deixa `archived` transicionar). O botão
         * aparecia nos 4 terminais e ali era beco sem saída — o operador confirmava e
         * recebia «Conversa não está fechada (current=archived)». Medido ao vivo 08/Ago/2026.
         */}
        {showReopen && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setReopenOpen(true)}
            title="Reabrir conversa encerrada"
          >
            Reabrir conversa
          </button>
        )}
      </div>

      {/* IdentityPopover enriquecido (r7b) */}
      {popoverOpen && (
        <IdentityPopover
          conversation={conversation}
          intentLabel={humanizeIntent(signals.primaryIntent ?? null)}
          onClose={() => setPopoverOpen(false)}
        />
      )}

      {/* KeyboardShortcutsModal (r7b §⌘/) */}
      <KeyboardShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* Fatia F — modal de encerramento estruturado (outcome + blockers). */}
      <ConversationCloseModal
        open={closeFlow.closeOpen}
        onClose={closeFlow.dismissClose}
        onSubmit={closeFlow.handleCloseSubmit}
        submitting={closeFlow.isClosing}
        followupEnabled={canFollowup}
        {...(closeFlow.closeBlocker !== undefined ? { blockerError: closeFlow.closeBlocker } : {})}
      />

      {/* Fatia G — modal de transferência/handoff (push para atendente ou pull para fila). */}
      <HandoffInitiateModal
        conversationId={conversation.id}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
      />

      {/* ADR-109 — modal de criação de pendência do atendente. */}
      <PendencyCreateModal
        conversationId={conversation.id}
        open={pendencyOpen}
        onClose={() => setPendencyOpen(false)}
      />

      {/* ADR-115 — modal de criação de agendamento (appointment). */}
      <AppointmentCreateModal
        conversationId={conversation.id}
        open={appointmentOpen}
        onClose={() => setAppointmentOpen(false)}
      />

      {/* §2.8 Item 297 — modal de confirmação para reabrir conversa terminal. */}
      <ConversationReopenModal
        conversationId={conversation.id}
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
      />

      {/* S7 PR3 (S6 Condução Governada, Q-J8) — troca manual de playbook. */}
      <PlaybookManualSwitchModal
        conversationId={conversation.id}
        currentPlaybookId={conversation.playbook?.id ?? null}
        open={manualSwitchOpen}
        onClose={() => setManualSwitchOpen(false)}
      />
    </header>
  );
}

function StatusInline({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  // §2.8 — estado terminal (read-only): dot cinza para qualquer terminal
  // (antes só 'closed'), badge do status ("Resolvida"/"Encerrada (não
  // resolvida)"/…) e horário do encerramento (closedAt) no lugar da atividade.
  const terminal = isTerminalStatus(conversation.status);
  // Brief 2A r7b canonical — .conv-header__status + .conv-header__status-dot.
  // Variante do dot por status: terminal → cinza, paused → warning, default → success.
  const dotVariant = terminal
    ? ' conv-header__status-dot--closed'
    : conversation.status === 'paused'
      ? ' conv-header__status-dot--paused'
      : '';
  const lastActivity = relativeTime(
    terminal && conversation.closedAt !== null ? conversation.closedAt : conversation.updatedAt,
  );
  // Badge de status seguindo o padrão da lista (StatusBadge — DS tokens inline por tone).
  const toneStyle = {
    neutral: { background: 'var(--bg-surface-2)', color: 'var(--fg-2)' },
    attention: { background: 'var(--warning-bg)', color: 'var(--warning-fg)' },
    positive: { background: 'var(--success-bg)', color: 'var(--success-fg)' },
    muted: { background: 'var(--bg-surface-2)', color: 'var(--fg-3)', opacity: 0.7 },
  } as const;
  return (
    <span className="conv-header__status">
      <span aria-hidden className={`conv-header__status-dot${dotVariant}`} />
      {terminal && (
        <span
          className="text-2xs uppercase tracking-wide font-medium px-1.5 py-0.5 rounded"
          style={toneStyle[statusTone(conversation.status)]}
        >
          {statusLabel(conversation.status)}
        </span>
      )}
      <span>{lastActivity}</span>
    </span>
  );
}

function ClassificationBadge({ level }: { level: number }): JSX.Element {
  // Fase C Brief 2A r7b — DS v2.3.0 canonical .class-badge:
  //   level 2 → class-badge--n2 (cinza stone)
  //   level 3 → class-badge--n3 (amber)
  //   level 4 → class-badge--n4 (red)
  const variant = level >= 2 && level <= 4 ? `class-badge--n${level}` : 'class-badge--n1';
  return (
    <span className={`class-badge ${variant}`} title={`Sensibilidade N${level.toString()} (LGPD)`}>
      <span className="class-badge__dot" aria-hidden />N{level}
    </span>
  );
}

function InlineAction({
  label,
  shortcut,
  Icon,
  onClick,
  disabled = false,
  disabledTitle,
}: {
  label: string;
  shortcut?: string;
  Icon?: LucideIcon;
  /** Quando ausente, a ação permanece `disabled` ("fatia futura"). */
  onClick?: () => void;
  /** Força disabled mesmo com `onClick` presente (e.g., conversa terminal §2.8). */
  disabled?: boolean;
  /** Override do tooltip quando o disabled veio de motivo externo (e.g., terminal). */
  disabledTitle?: string;
}): JSX.Element {
  // Brief 2A r7b canonical — .icon-btn (28x28 ghost). Title traz label + shortcut.
  const futureFatia = onClick === undefined;
  const isDisabled = futureFatia || disabled;
  const title =
    isDisabled && disabledTitle !== undefined
      ? disabledTitle
      : `${label}${shortcut !== undefined ? ` (${shortcut})` : ''}${
          futureFatia ? ' — disponível em fatia futura' : ''
        }`;
  return (
    <button
      type="button"
      disabled={isDisabled}
      {...(onClick !== undefined && !disabled && { onClick })}
      className="icon-btn"
      title={title}
      aria-label={label}
    >
      {Icon !== undefined ? (
        <Icon className="icon" size={16} aria-hidden />
      ) : (
        <span>{label.slice(0, 1)}</span>
      )}
    </button>
  );
}

function MoreMenu({ onOpenShortcuts }: { onOpenShortcuts: () => void }): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        className="icon-btn"
        aria-label="Mais ações"
        aria-expanded={menuOpen}
        title="Mais ações"
      >
        <MoreHorizontal className="icon" size={16} aria-hidden />
      </button>

      {menuOpen && (
        <div
          className="absolute top-full right-0 mt-1 w-48 rounded-md border border-border-subtle bg-surface shadow-lg z-20 py-1 text-sm"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-3 py-2 text-text-secondary hover:bg-surface-raised flex items-center justify-between gap-2"
            onClick={() => {
              setMenuOpen(false);
              onOpenShortcuts();
            }}
          >
            <span>Atalhos de teclado</span>
            <kbd className="text-2xs font-mono border border-border-subtle rounded px-1 text-text-secondary">
              ⌘/
            </kbd>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled
            className="w-full text-left px-3 py-2 text-text-secondary hover:bg-surface-raised opacity-60 cursor-not-allowed"
          >
            Ver audit log…
          </button>
        </div>
      )}
    </div>
  );
}

function CloseConversationButton({ onClick }: { onClick: () => void }): JSX.Element {
  // Brief 2A r7b canonical — .btn--header-close (destructive Encerrar).
  // Sem estado desabilitado: em conversa terminal o botão não é renderizado (§2.8
  // revisado 08/Ago) — não existe "Encerrar cinza" para explicar com tooltip.
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn--header-close"
      title="Encerrar conversa (⌘W)"
    >
      <X className="icon" size={14} aria-hidden /> Encerrar
    </button>
  );
}

/** Intent do classification-agent chega como slug (`relatar_problema_internet`) — humaniza p/ exibição. */
function humanizeIntent(slug: string | null): string | null {
  if (slug === null || slug.length === 0) return null;
  const words = slug.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function IdentityPopover({
  conversation,
  intentLabel,
  onClose,
}: {
  conversation: ConversationDetail;
  /** Intent primária detectada (classification-agent) — null quando ausente. */
  intentLabel: string | null;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const canViewPerson = useCapability('person-read');
  // Item 391-B — operador confirma "é o titular" (gated por capability).
  const canConfirmIdentity = useCapability('customer-registry-confirm');
  const [confirmIdentityOpen, setConfirmIdentityOpen] = useState(false);
  const personName = conversation.person?.displayName ?? '(sem identidade)';
  const channel = conversation.identity?.channel ?? conversation.channel;
  const identifier = conversation.identity?.channelIdentifier ?? null;
  const verified = conversation.identity?.verified === true;
  // Item 391 — selo de confiança de identidade (verified vence; senão deriva da
  // proveniência: self_declared = autodeclarada; observed/ausente = não identificada).
  const provenance = conversation.identity?.provenance;
  const assuranceLabel = verified
    ? 'Identidade verificada'
    : provenance === 'self_declared'
      ? 'Identidade autodeclarada'
      : 'Identidade não confirmada';
  const hasIdentity = conversation.identity != null;
  // Botão de confirmação só faz sentido quando há identidade e ela ainda não é verificada.
  const showConfirmIdentity = canConfirmIdentity && hasIdentity && !verified;
  const sessionLabel =
    conversation.metaSessionType !== null && conversation.metaSessionType !== undefined
      ? (SESSION_LABELS[conversation.metaSessionType] ?? conversation.metaSessionType)
      : null;
  const hasBond = conversation.bondId !== null;
  const personId = conversation.person?.id;
  const canOpenFicha = personId !== undefined && canViewPerson;
  // Item 391-D — papel do contato no contrato (só busca quando há vínculo).
  const { data: contactRoleData } = useContactRole(conversation.id, hasBond);

  // Footer: "Em atendimento há 24min · atribuído a Carolina Mendes" (r7b).
  const assigned = conversation.assignedToUser;
  const footerLine = `${statusLabel(conversation.status)} ${relativeTime(conversation.updatedAt)}${
    assigned !== null && assigned !== undefined ? ` · atribuído a ${assigned.name}` : ''
  }`;

  return (
    <div
      role="dialog"
      aria-label="Identidade do cliente"
      className="popover absolute top-full left-12 mt-1 z-10"
      style={{ width: 300 }}
    >
      <div className="popover__body">
        <div className="identity-popover__top">
          <span
            className="avatar avatar--lg"
            style={{ background: 'var(--bg-surface-2)', color: 'var(--fg-2)' }}
          >
            {initialsOf(personName)}
          </span>
          <div className="identity-popover__id">
            <span className="identity-popover__name">{personName}</span>
            <span className="identity-popover__phone">{identifier ?? channelLabel(channel)}</span>
          </div>
        </div>
      </div>

      <div style={{ padding: 'var(--space-3)', borderTop: '1px solid var(--border-subtle)' }}>
        <div className="identity-popover__row-label">Canal e sessão</div>
        <div className="identity-popover__row">
          <span
            className="badge"
            title={verified ? `${channelLabel(channel)} · Verificado` : channelLabel(channel)}
          >
            <MessageCircle size={10} aria-hidden /> {channelLabel(channel)}
          </span>
          {sessionLabel !== null && conversation.metaSessionIsExpired !== true && (
            <span className="badge badge--success">
              <span className="badge__dot" aria-hidden /> {sessionLabel}
            </span>
          )}
          {sessionLabel !== null && conversation.metaSessionIsExpired === true && (
            <span className="badge badge--mono" title={sessionLabel}>
              <Clock size={10} aria-hidden /> sessão expirada
            </span>
          )}
        </div>

        <div className="identity-popover__row-label">Confiança de identidade</div>
        <div
          className="identity-popover__row"
          style={{ alignItems: 'center', gap: 'var(--space-2)' }}
        >
          <span
            className={verified ? 'badge badge--success' : 'badge badge--mono'}
            title={
              verified
                ? 'Verificada por posse/operador — confiança ampliada'
                : provenance === 'self_declared'
                  ? 'O cliente declarou na conversa; ainda não verificado'
                  : 'Sem identificação confirmada'
            }
          >
            <ShieldCheck size={10} aria-hidden /> {assuranceLabel}
          </span>
          {showConfirmIdentity && (
            <button
              type="button"
              className="identity-popover__confirm-link"
              style={{
                marginLeft: 'auto',
                fontSize: 'var(--text-xs)',
                color: 'var(--brand)',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                textDecoration: 'underline',
              }}
              onClick={() => setConfirmIdentityOpen(true)}
            >
              Confirmar titular
            </button>
          )}
        </div>

        {(hasBond || intentLabel !== null) && (
          <>
            <div className="identity-popover__row-label">Vínculo e intent</div>
            <div className="identity-popover__row">
              {hasBond && (
                <span className="badge" title="Conversa vinculada a um Bond">
                  <Briefcase size={10} aria-hidden /> Vínculo ativo
                </span>
              )}
              {contactRoleData?.hasBond === true && contactRoleData.role !== 'unknown' && (
                <span
                  className={contactRoleData.isTitular ? 'badge badge--brand' : 'badge badge--mono'}
                  title={`Papel do contato no vínculo${
                    contactRoleData.authorizedScopes.length > 0
                      ? ` · escopos: ${contactRoleData.authorizedScopes.join(', ')}`
                      : ''
                  }`}
                >
                  <UserCheck size={10} aria-hidden /> {contactRoleData.label}
                </span>
              )}
              {/*
               * Motor Neutro D1 — o badge de papel conflava TRÊS situações num rótulo só
               * («Autorizado»): autorizado com poder real, autorizado cuja autorização EXPIROU, e
               * autorizado com escopo que não gateia nada. Só as duas últimas têm conserto, e
               * cada uma tem um conserto DIFERENTE. Os dois sinais são disjuntos por construção
               * (mandato caducado devolve lista vazia ⇒ não há escopo inerte a listar), logo
               * ocupam o mesmo lugar sem competir. Fecha R-B6-e + a perna UI da R-C1-e.
               */}
              {contactRoleData?.authorizedScopesForfeited === true && (
                <span
                  className="badge badge--warning"
                  title="O mandato deste contato venceu — os escopos deixaram de valer no gate. Renovar a validade na aba Cliente restaura a autorização."
                >
                  <Clock size={10} aria-hidden /> autorização expirada
                </span>
              )}
              {contactRoleData?.authorizedScopesIneffective !== undefined &&
                contactRoleData.authorizedScopesIneffective.length > 0 && (
                  <span
                    className="badge badge--warning"
                    title={`Escopo concedido que nenhuma ação da plataforma exige: ${contactRoleData.authorizedScopesIneffective.join(
                      ', ',
                    )} — a autorização existe, mas não amplia o que este contato pode fazer.`}
                  >
                    <AlertTriangle size={10} aria-hidden /> escopo sem efeito
                  </span>
                )}
              {intentLabel !== null && (
                <span className="badge badge--brand">
                  <Tag size={10} aria-hidden /> {intentLabel}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <div className="popover__footer">
        <div className="identity-popover__assigned" style={{ marginBottom: 'var(--space-2)' }}>
          <span className="identity-popover__assigned-dot" aria-hidden />
          <span>{footerLine}</span>
        </div>
        <button
          type="button"
          className="identity-popover__cta"
          disabled={!canOpenFicha}
          style={canOpenFicha ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
          title={
            canOpenFicha
              ? 'Abrir ficha da pessoa'
              : 'Sem pessoa vinculada ou sem permissão para ver fichas'
          }
          onClick={() => {
            if (personId !== undefined) navigate(`/pessoas/${personId}`);
          }}
        >
          Ver ficha completa
          <ArrowUpRight size={12} aria-hidden />
        </button>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="absolute top-2 right-2 text-text-secondary hover:text-text-primary text-sm"
        aria-label="Fechar"
      >
        ×
      </button>

      {showConfirmIdentity && (
        <ConfirmCustomerIdentityModal
          conversationId={conversation.id}
          personName={personName}
          open={confirmIdentityOpen}
          onClose={() => setConfirmIdentityOpen(false)}
        />
      )}
    </div>
  );
}
